/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { requestStatusType } from '../../types';
import {
  finalizeUploads,
  getUploadUrls,
} from '../../services/fileSystemService';
import { ContentType } from '../../constants/mediaFormatProfiles';
import { addFile } from '../library/librarySlice';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import {
  initializeUploadWithDebounce,
  finalizeUploadWithRetry,
  uploadSingleChunk,
} from '../../recorder/utils/uploadUtils';
import { abortMultipartUpload } from '../../recorder/uploadService';
import validateFiles from './fileValidation';
import { shouldRefreshToken } from '../../lib/localStorageHandler';
import { refreshToken } from '../auth/authThunks';
import getTranslation from '../../i18n/utils';
import type { RootState } from '../../reducers';

interface UploadProgress {
  teamId: string;
  folderId: string;
  fileId: string;
  fileName: string;
  progress: number; // Progress in percentage
  previewUrl?: string; // Object URL for preview
  fileType?: string; // MIME type
  isMultipart: boolean;
  uploadId?: string;
}

/**
 * Module-level registry for AbortControllers, keyed by fileId.
 * Not in Redux because AbortControllers are not serializable.
 */
const uploadAbortControllers = new Map<string, AbortController>();

interface UploadState {
  status: requestStatusType;
  progress: number; // Total progress
  activeUploads: UploadProgress[];
}

const initialState: UploadState = {
  status: 'idle',
  progress: 0,
  activeUploads: [],
};

// Custom action for progress
export const updateProgress = createAction<UploadProgress>('upload/progress');

// Define the thunk for uploading files
interface UploadFilesThunkArg {
  teamId: string;
  folderId: string;
  files: File[];
  sourceAssetId?: string;
}

const MAX_DIRECT_UPLOAD_SIZE = 100 * 1024 * 1024; // 100 MiB
const UPLOAD_CHUNK_SIZE = 20 * 1024 * 1024; // 20 MiB

/**
 * Cancel/abort an in-progress file upload.
 * Aborts the HTTP request, removes from active uploads,
 * and calls backend cleanup for multipart uploads.
 */
export const cancelUpload = createAsyncThunk(
  'upload/cancelUpload',
  async (
    {
      fileId,
      reasonCode = 'user_cancelled_file_upload',
      reason,
      reasonDetail,
    }: {
      fileId: string;
      reasonCode?: string;
      reason?: string;
      reasonDetail?: string;
    },
    { getState },
  ) => {
    // 1. Abort the in-flight HTTP request
    const controller = uploadAbortControllers.get(fileId);
    if (controller) {
      controller.abort();
      uploadAbortControllers.delete(fileId);
    }

    // 2. If multipart, call backend to abort S3 multipart upload
    const state = (getState() as RootState).upload;
    const upload = state.activeUploads.find((u) => u.fileId === fileId);

    if (upload?.isMultipart && upload.uploadId) {
      try {
        await abortMultipartUpload({
          teamId: upload.teamId,
          folderId: upload.folderId,
          fileId,
          uploadId: upload.uploadId,
          contentType: upload.fileType || 'video/x-matroska',
          reason,
          reasonCode,
          reasonDetail,
        });
      } catch {
        // Best-effort cleanup; the abort already stopped the upload
      }
    }

    // 3. Clean up object URL
    if (upload?.previewUrl) {
      URL.revokeObjectURL(upload.previewUrl);
    }

    return { fileId };
  },
);

export const uploadFiles = createAsyncThunk(
  'upload/uploadFiles',
  async (
    { teamId, folderId, files, sourceAssetId }: UploadFilesThunkArg,
    { dispatch, rejectWithValue },
  ) => {
    if (shouldRefreshToken()) {
      await dispatch(refreshToken());
    }

    const validationResult = await validateFiles(files);

    if (!validationResult.isValid) {
      // Early return if validation fails
      return rejectWithValue({
        isValid: false,
        errorMessage: validationResult.errorMessage,
        errorType: validationResult.errorType,
      });
    }

    const filesNeedingConversion =
      validationResult.filesNeedingConversion || [];

    for (const file of files) {
      const fileSize = file.size;
      const fileName = file.name;
      const contentType = (file.type as ContentType) || 'video/x-matroska';
      const previewUrl = URL.createObjectURL(file);
      const isMultipart = fileSize > MAX_DIRECT_UPLOAD_SIZE;
      const abortController = new AbortController();

      if (isMultipart) {
        // Multipart upload for large files
        try {
          const { uploadId, fileId } = await initializeUploadWithDebounce({
            teamId,
            folderId,
            contentType,
            source: 'multipart-upload-file-web',
          });

          uploadAbortControllers.set(fileId, abortController);

          const totalChunks = Math.ceil(fileSize / UPLOAD_CHUNK_SIZE);

          for (let i = 0; i < totalChunks; i += 1) {
            if (abortController.signal.aborted) {
              throw new DOMException('Upload aborted', 'AbortError');
            }

            const start = i * UPLOAD_CHUNK_SIZE;
            const end = Math.min(start + UPLOAD_CHUNK_SIZE, fileSize);
            const chunk = file.slice(start, end);
            const partNumber = i + 1;

            await uploadSingleChunk({
              teamId,
              folderId,
              fileId,
              uploadId,
              contentType,
              blob: chunk,
              partNumber,
              signal: abortController.signal,
            });

            if (!abortController.signal.aborted) {
              const progress = Math.round(((i + 1) * 100) / totalChunks);
              dispatch(
                updateProgress({
                  teamId,
                  folderId,
                  fileId,
                  fileName,
                  progress,
                  previewUrl,
                  fileType: contentType,
                  isMultipart: true,
                  uploadId,
                }),
              );
            }
          }

          const teamFileEntity = await finalizeUploadWithRetry({
            teamId,
            folderId,
            fileId,
            uploadId,
            contentType,
            notes: [],
            title: fileName,
            needsConversion: filesNeedingConversion.includes(file),
          });
          dispatch(addFile(teamFileEntity));
        } catch (error) {
          if (!abortController.signal.aborted) {
            dispatch(
              setAlertMessage({
                message: getTranslation('upload.errors.uploadFailed'),
                type: 'error',
              }),
            );
            return rejectWithValue(error);
          }
          // Cancelled by user — skip to next file
        } finally {
          uploadAbortControllers.delete(
            // fileId may not be in scope if init failed, so clean up by controller
            [...uploadAbortControllers.entries()].find(
              ([, c]) => c === abortController,
            )?.[0] ?? '',
          );
        }
      } else {
        // Single presigned URL for smaller files
        try {
          const response = await getUploadUrls({
            teamId,
            folderId,
            files: [{ name: fileName, contentType }],
          });

          const { fileId, uploadUrl } = response.data.data.uploadParams[0];

          uploadAbortControllers.set(fileId, abortController);

          // Retry logic for non-multipart uploads (max 3 attempts)
          let uploadSuccess = false;
          let lastError = null;
          const maxRetries = 3;

          for (let attempt = 0; attempt < maxRetries; attempt += 1) {
            if (abortController.signal.aborted) {
              throw new DOMException('Upload aborted', 'AbortError');
            }
            await new Promise((resolve) =>
              setTimeout(resolve, attempt * 1000 + 100),
            );
            try {
              await axios.put(uploadUrl, file, {
                headers: {
                  'Content-Type': contentType,
                },
                signal: abortController.signal,
                onUploadProgress: (progressEvent) => {
                  if (abortController.signal.aborted) return;
                  const { loaded, total } = progressEvent || {};
                  if (!loaded || !total) return;
                  const progress = Math.round((loaded * 100) / total);
                  dispatch(
                    updateProgress({
                      teamId,
                      folderId,
                      fileId,
                      fileName,
                      progress,
                      previewUrl,
                      fileType: contentType,
                      isMultipart: false,
                    }),
                  );
                },
              });
              uploadSuccess = true;
              break; // Exit retry loop on success
            } catch (uploadError) {
              if (abortController.signal.aborted) throw uploadError;
              lastError = uploadError;
              if (attempt < maxRetries) {
                // Reset progress on retry
                dispatch(
                  updateProgress({
                    teamId,
                    folderId,
                    fileId,
                    fileName,
                    progress: 0,
                    previewUrl,
                    fileType: contentType,
                    isMultipart: false,
                  }),
                );
              }
            }
          }

          if (!uploadSuccess) {
            throw lastError;
          }

          const finalizeResponse = await finalizeUploads({
            teamId,
            folderId,
            fileId,
            contentType,
            name: fileName,
            needsConversion: filesNeedingConversion.includes(file),
            ...(sourceAssetId && { sourceAssetId }),
          });

          dispatch(addFile(finalizeResponse.data.data.file));
        } catch (error) {
          if (!abortController.signal.aborted) {
            dispatch(
              setAlertMessage({
                message: getTranslation('upload.errors.uploadFailed'),
                type: 'error',
              }),
            );
            return rejectWithValue(error);
          }
          // Cancelled by user — skip to next file
        } finally {
          uploadAbortControllers.delete(
            [...uploadAbortControllers.entries()].find(
              ([, c]) => c === abortController,
            )?.[0] ?? '',
          );
        }
      }
    }
    // Always return a value at the end
    return { success: true };
  },
);

const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    resetUploadState: (state) => {
      state.status = 'idle';
      state.progress = 0;
      state.activeUploads = [];
    },
    // Used in recorder
    addFileToUploads: (
      state,
      action: PayloadAction<{
        teamId?: string;
        folderId: string;
        fileId: string;
        fileName: string;
        progress: number;
      }>,
    ) => {
      state.activeUploads.push({
        ...action.payload,
        teamId: action.payload.teamId ?? '',
        isMultipart: true,
      });
    },
    // Used in recorder
    removeFileFromUploads: (
      state,
      action: PayloadAction<{
        folderId: string;
        fileId: string;
      }>,
    ) => {
      state.activeUploads = state.activeUploads.filter(
        (up) => up.fileId !== action.payload.fileId,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(uploadFiles.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(uploadFiles.fulfilled, (state) => {
      state.status = 'succeeded';
      state.progress = 100; // All uploads complete
      state.progress = 0;
      // Clean up object URLs
      state.activeUploads.forEach((upload) => {
        if (upload.previewUrl) {
          URL.revokeObjectURL(upload.previewUrl);
        }
      });
      state.activeUploads = [];
    });
    builder.addCase(uploadFiles.rejected, (state) => {
      state.status = 'failed';
      state.progress = 0;
      // Clean up object URLs
      state.activeUploads.forEach((upload) => {
        if (upload.previewUrl) {
          URL.revokeObjectURL(upload.previewUrl);
        }
      });
      state.activeUploads = [];
    });
    builder.addCase(cancelUpload.fulfilled, (state, action) => {
      const { fileId } = action.payload;
      state.activeUploads = state.activeUploads.filter(
        (u) => u.fileId !== fileId,
      );
      if (state.activeUploads.length > 0) {
        const totalProgress = state.activeUploads.reduce(
          (total, file) => total + file.progress,
          0,
        );
        state.progress = totalProgress / state.activeUploads.length;
      } else {
        state.progress = 0;
      }
    });
    builder.addCase(updateProgress, (state, action) => {
      const {
        teamId,
        folderId,
        fileId,
        progress,
        previewUrl,
        fileType,
        isMultipart,
        uploadId,
      } = action.payload;
      const existingProgress = state.activeUploads.find(
        (up) => up.fileId === fileId,
      );

      if (existingProgress) {
        existingProgress.progress = progress;
        if (previewUrl && !existingProgress.previewUrl) {
          existingProgress.previewUrl = previewUrl;
          existingProgress.fileType = fileType;
        }
        if (uploadId && !existingProgress.uploadId) {
          existingProgress.uploadId = uploadId;
        }
      } else {
        state.activeUploads.push({
          teamId,
          folderId,
          fileId,
          fileName: action.payload.fileName,
          progress,
          previewUrl,
          fileType,
          isMultipart: isMultipart ?? false,
          uploadId,
        });
      }

      // Calculate total progress
      const totalProgress = state.activeUploads.reduce(
        (total, file) => total + file.progress,
        0,
      );
      state.progress = totalProgress / state.activeUploads.length;
    });
  },
});

export const { resetUploadState, addFileToUploads, removeFileFromUploads } =
  uploadSlice.actions;

export default uploadSlice.reducer;
